import { CancelToken } from "axios";
import { axiosInstance } from "./ServiceConstants";
import { IRiderDetail, IRiderExtended, IRiderRead, Lap } from "../models/IRider";
import DateHelperInstance from "./DateHelper";

type RiderDetailPayload = IRiderDetail & {
  Laps?: IRiderDetail["laps"];
  BestLapId?: number;
  EventName?: string;
  CircuitBest?: number;
  GroupBest?: number;
  EventBest?: number;
  AverageMs?: number;
};

const buildRoundDetailFromLaps = (laps: Lap[]): Partial<IRiderDetail> => {
  const bestLap = laps.reduce<Lap | undefined>((currentBest, lap) => {
    if (!currentBest || lap.timeMs < currentBest.timeMs) {
      return lap;
    }
    return currentBest;
  }, undefined);
  const totalTimeMs = laps.reduce((total, lap) => total + lap.timeMs, 0);
  const averageMs = laps.length > 0 ? Math.round(totalTimeMs / laps.length) : undefined;
  const lastLap = laps[laps.length - 1];

  return {
    laps,
    lapCount: laps.length,
    bestLapId: bestLap?.lapId ?? 0,
    bestTime: bestLap?.timeFormatted ?? "00:00",
    lastTime: lastLap?.timeFormatted ?? "00:00",
    averageMs,
    averageTime: averageMs !== undefined
      ? DateHelperInstance.millisToMinutesSecondsAndMilliseconds(averageMs)
      : "00:00",
  };
};

const normalizeRiderDetailPayload = (payload: unknown): Partial<IRiderDetail> => {
  if (Array.isArray(payload)) {
    return buildRoundDetailFromLaps(payload as Lap[]);
  }

  const wrappedPayload = payload as {
    rider?: RiderDetailPayload;
    participant?: RiderDetailPayload;
    data?: RiderDetailPayload;
    result?: RiderDetailPayload;
  };

  const riderPayload = wrappedPayload?.rider
    ?? wrappedPayload?.participant
    ?? wrappedPayload?.data
    ?? wrappedPayload?.result
    ?? payload as RiderDetailPayload;

  return {
    ...riderPayload,
    laps: riderPayload?.laps ?? riderPayload?.Laps,
    bestLapId: riderPayload?.bestLapId ?? riderPayload?.BestLapId ?? 0,
    eventName: riderPayload?.eventName ?? riderPayload?.EventName ?? "",
    circuitBest: riderPayload?.circuitBest ?? riderPayload?.CircuitBest,
    groupBest: riderPayload?.groupBest ?? riderPayload?.GroupBest,
    eventBest: riderPayload?.eventBest ?? riderPayload?.EventBest,
    averageMs: riderPayload?.averageMs ?? riderPayload?.AverageMs,
  };
};
class RiderService {
  async getRiderDetail(eventId: string, tagId: string): Promise<IRiderDetail> {
    let riderResponse = await axiosInstance.get<IRiderDetail>(
      `api/rider/${eventId}/${tagId}`
    );
    return riderResponse.data;
  }

  async getRiderRefresh(
    eventId: string,
    tagId: string
  ): Promise<IRiderExtended> {
    let riderRefresh = await axiosInstance.get<IRiderExtended>(
      `api/rider/${eventId}/${tagId}/refresh`
    );
    return riderRefresh.data;
  }

  async reorganizeRider(
    eventId: string,
    tagId: string,
    trial: boolean,
    group: string
  ): Promise<IRiderExtended> {
    let reorganizeResponse = await axiosInstance.put<IRiderExtended>(`api/rider/${eventId}/${tagId}/organize`, {
      raceId: eventId,
      tagId: tagId,
      trial: trial,
      group: group,
    });
    return reorganizeResponse.data;
  }

  async getRiderReads(
    raceId: string,
    tagId: string,
    cancelToken?: CancelToken
  ): Promise<IRiderRead[]> {
    const response = await axiosInstance.get<IRiderRead[]>(
      `api/rider/${raceId}/${tagId}/reads`,
      cancelToken ? { cancelToken } : undefined
    );
    return response.data;
  }

  async deleteRiderRead(
    raceId: string,
    epc: string,
    tagSeenTime: number
  ): Promise<void> {
    await axiosInstance.delete(
      `api/rider/${raceId}/${encodeURIComponent(epc)}/reads/${encodeURIComponent(String(tagSeenTime))}`
    );
  }

  async recalculateRiderLaps(raceId: string, tagId: string): Promise<void> {
    await axiosInstance.post(`api/Rider/${raceId}/${tagId}/recalculate-laps`);
  }

  async getRiderDetailForRound(eventId: string, tagId: string, roundId: string): Promise<Partial<IRiderDetail>> {
    const result = await axiosInstance.get<unknown>(
      `api/Rider/${eventId}/${tagId}/${roundId}`
    );
    return normalizeRiderDetailPayload(result.data);
  }
}

const RiderServiceInstance = new RiderService();

export default RiderServiceInstance;
